import { useState } from 'react';
import { useLazyGetFileUrlQuery } from '../redux/services/file';
import { downloadFile, shouldRefreshUrl } from '../utils/dom-utils';
import { useApiLogger } from './use-api-logger';

interface UseFileDownloadOptions {
  fileId: string;
  fileName: string;
  currentUrl: string;
}

interface UseFileDownloadReturn {
  handleDownload: () => Promise<void>;
  isDownloading: boolean;
}

/**
 * Hook to handle file downloads with automatic presigned URL refresh.
 *
 * Checks if the current presigned URL is expired and fetches a fresh one if needed.
 * Falls back to the current URL if fetching a fresh URL fails.
 *
 * @param options - Configuration options
 * @param options.fileId - The ID of the file to download
 * @param options.fileName - The name to save the file as
 * @param options.currentUrl - The current presigned URL for the file
 * @returns Object containing the download handler and loading state
 */
export const useFileDownload = ({ fileId, fileName, currentUrl }: UseFileDownloadOptions): UseFileDownloadReturn => {
  const [getFileUrl, { isLoading }] = useLazyGetFileUrlQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  const log = useApiLogger();

  const handleDownload = async () => {
    if (isDownloading || isLoading) return;

    try {
      setIsDownloading(true);

      if (shouldRefreshUrl(currentUrl)) {
        const freshUrl = await getFileUrl(fileId).unwrap();
        downloadFile(freshUrl, fileName);
      } else {
        // Use the existing URL if it's still valid
        downloadFile(currentUrl, fileName);
      }
    } catch (error) {
      log('Failed to download attachment file', { error });
      // Fallback to original URL if fresh URL fetch fails
      try {
        downloadFile(currentUrl, fileName);
      } catch (fallbackError) {
        log('Failed to download attachment file with fallback url', { error, fallbackError });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    handleDownload,
    isDownloading,
  };
};
